/**
 * Manipulation Tester Component
 * 
 * Test a manipulation profile against a SIP message to preview transformations.
 */

import React, { useState } from 'react';
import {
  X,
  TestTube2,
  RefreshCw,
  Play,
  Check,
  XCircle,
  ArrowRight,
  Info,
  AlertCircle,
  Copy
} from 'lucide-react';
import type { Sip } from '@sbclite/core';
import { useTestSipManipulation } from '../../hooks/useSipManipulations';

// ========================================
// TYPES
// ========================================

interface ManipulationTesterProps {
  profile: Sip.ManipulationInfo;
  onClose: () => void;
}

interface TestMessage {
  from: string;
  to: string;
  requestUri?: string;
  pAssertedIdentity?: string;
  remotePartyId?: string;
  contact?: string;
  via?: string;
}

// ========================================
// PRESET EXAMPLES
// ========================================

const PRESET_EXAMPLES: { name: string; message: TestMessage }[] = [
  {
    name: 'US to E.164 Call',
    message: {
      from: '"John Doe" <sip:+14155551234@carrier.example.com;user=phone>',
      to: '<sip:+14155559876@carrier.example.com;user=phone>',
      requestUri: 'sip:+14155559876@sbc.example.com'
    }
  },
  {
    name: 'UK Number Call',
    message: {
      from: '"Jane Smith" <sip:+442071234567@uk-carrier.example.com>',
      to: '<sip:+442079876543@uk-carrier.example.com>',
      requestUri: 'sip:+442079876543@sbc.example.com'
    }
  },
  {
    name: 'Internal Extension',
    message: {
      from: '"Reception" <sip:100@pbx.internal>',
      to: '<sip:200@pbx.internal>',
      requestUri: 'sip:200@pbx.internal'
    }
  },
  {
    name: 'Teams Call',
    message: {
      from: '"Teams User" <sip:user@domain.onmicrosoft.com>',
      to: '<sip:+14155551234@sbc.example.com;user=phone>',
      requestUri: 'sip:+14155551234@sbc.example.com',
      pAssertedIdentity: '"Teams User" <sip:user@domain.onmicrosoft.com>'
    }
  }
];

// ========================================
// MAIN COMPONENT
// ========================================

export function ManipulationTester({ profile, onClose }: ManipulationTesterProps) {
  // Test message state
  const [message, setMessage] = useState<TestMessage>({
    from: '"John Doe" <sip:+14155551234@example.com>',
    to: '<sip:+14155559876@example.com>',
    requestUri: 'sip:+14155559876@sbc.example.com'
  });

  // Test mutation
  const testMutation = useTestSipManipulation();
  const result = testMutation.data?.data;

  // Run test
  const runTest = () => {
    testMutation.mutate({
      manipulationId: profile.id,
      message
    });
  };

  // Apply preset
  const applyPreset = (preset: TestMessage) => {
    setMessage(preset);
    testMutation.reset();
  };

  // Copy to clipboard
  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  // Get display value for a field
  const getFieldValue = (field: keyof TestMessage): string => {
    return message[field] || '';
  };

  // Update field
  const updateField = (field: keyof TestMessage, value: string) => {
    setMessage(prev => ({
      ...prev,
      [field]: value || undefined
    }));
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-5xl w-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="p-6 border-b border-gray-200 flex-shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-purple-100">
                <TestTube2 className="w-6 h-6 text-purple-600" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-gray-900">
                  Test Manipulation Profile
                </h2>
                <p className="text-sm text-gray-600">
                  Testing: <span className="font-medium">{profile.name}</span>
                  <span className="text-gray-400 mx-2">•</span>
                  <span className="text-gray-500">{profile.rules.length} rules</span>
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto flex-1">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Input Section */}
            <div>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">Test Message</h3>
                <select
                  onChange={(e) => {
                    const preset = PRESET_EXAMPLES.find(p => p.name === e.target.value);
                    if (preset) applyPreset(preset.message);
                  }}
                  className="text-sm border border-gray-300 rounded-lg px-3 py-1.5"
                  defaultValue=""
                >
                  <option value="" disabled>Load preset...</option>
                  {PRESET_EXAMPLES.map(p => (
                    <option key={p.name} value={p.name}>{p.name}</option>
                  ))}
                </select>
              </div>

              <div className="space-y-4">
                {/* From */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    From Header <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={getFieldValue('from')}
                    onChange={(e) => updateField('from', e.target.value)}
                    placeholder='"Display Name" <sip:user@domain>'
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg font-mono text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                  />
                </div>

                {/* To */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    To Header <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={getFieldValue('to')}
                    onChange={(e) => updateField('to', e.target.value)}
                    placeholder="<sip:user@domain>"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg font-mono text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                  />
                </div>

                {/* Request-URI */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Request-URI
                  </label>
                  <input
                    type="text"
                    value={getFieldValue('requestUri')}
                    onChange={(e) => updateField('requestUri', e.target.value)}
                    placeholder="sip:user@domain"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg font-mono text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                  />
                </div>

                {/* P-Asserted-Identity */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    P-Asserted-Identity
                  </label>
                  <input
                    type="text"
                    value={getFieldValue('pAssertedIdentity')}
                    onChange={(e) => updateField('pAssertedIdentity', e.target.value)}
                    placeholder='"Display Name" <sip:user@domain>'
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg font-mono text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                  />
                </div>

                {/* Remote-Party-ID */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Remote-Party-ID
                  </label>
                  <input
                    type="text"
                    value={getFieldValue('remotePartyId')}
                    onChange={(e) => updateField('remotePartyId', e.target.value)}
                    placeholder='"Display Name" <sip:user@domain>'
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg font-mono text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                  />
                </div>

                {/* Contact */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Contact
                  </label>
                  <input
                    type="text"
                    value={getFieldValue('contact')}
                    onChange={(e) => updateField('contact', e.target.value)}
                    placeholder="<sip:user@host:port>"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg font-mono text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                  />
                </div>
              </div>

              <button
                onClick={runTest}
                disabled={testMutation.isPending || !message.from || !message.to}
                className="mt-6 w-full px-4 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {testMutation.isPending ? (
                  <>
                    <RefreshCw className="w-5 h-5 animate-spin" />
                    Testing...
                  </>
                ) : (
                  <>
                    <Play className="w-5 h-5" />
                    Run Test
                  </>
                )}
              </button>
            </div>

            {/* Results Section */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">Results</h3>

              {testMutation.error ? (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <div className="flex items-center gap-2 text-red-800">
                    <AlertCircle className="w-5 h-5" />
                    <span className="font-medium">Test Failed</span>
                  </div>
                  <p className="text-sm text-red-700 mt-2">
                    {testMutation.error instanceof Error 
                      ? testMutation.error.message 
                      : 'An error occurred while testing'}
                  </p>
                </div>
              ) : !result ? (
                <div className="bg-gray-50 rounded-lg p-8 text-center">
                  <TestTube2 className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                  <p className="text-gray-500">
                    Enter a test message and click "Run Test" to see the transformation results
                  </p>
                </div>
              ) : (
                <div className="space-y-6">
                  {/* Before/After Comparison */}
                  <div>
                    <h4 className="text-sm font-medium text-gray-700 mb-3">Transformation Results</h4>
                    <div className="bg-gray-50 rounded-lg divide-y divide-gray-200">
                      {Object.entries(result.original).map(([key, originalValue]) => {
                        if (!originalValue) return null;
                        const transformedValue = result.transformed[key as keyof typeof result.transformed];
                        const changed = originalValue !== transformedValue;

                        return (
                          <div key={key} className="p-3">
                            <div className="flex items-center justify-between mb-2">
                              <span className="text-sm font-medium text-gray-700 capitalize">
                                {key.replace(/([A-Z])/g, ' $1').trim()}
                              </span>
                              {changed ? (
                                <span className="text-xs text-green-600 font-medium flex items-center gap-1">
                                  <Check className="w-3 h-3" />
                                  Modified
                                </span>
                              ) : (
                                <span className="text-xs text-gray-400">Unchanged</span>
                              )}
                            </div>
                            
                            {changed ? (
                              <div className="space-y-2">
                                <div className="flex items-start gap-2">
                                  <span className="text-xs text-gray-400 mt-1 w-12">Before:</span>
                                  <code className="flex-1 text-xs bg-red-50 text-red-700 px-2 py-1 rounded break-all">
                                    {originalValue}
                                  </code>
                                </div>
                                <div className="flex items-start gap-2">
                                  <span className="text-xs text-gray-400 mt-1 w-12">After:</span>
                                  <code className="flex-1 text-xs bg-green-50 text-green-700 px-2 py-1 rounded break-all">
                                    {transformedValue}
                                  </code>
                                  <button
                                    onClick={() => copyToClipboard(transformedValue || '')}
                                    className="text-gray-400 hover:text-gray-600"
                                  >
                                    <Copy className="w-3 h-3" />
                                  </button>
                                </div>
                              </div>
                            ) : (
                              <code className="text-xs bg-white text-gray-600 px-2 py-1 rounded block break-all">
                                {originalValue}
                              </code>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Rule Results */}
                  <div>
                    <h4 className="text-sm font-medium text-gray-700 mb-3">
                      Rule Matching ({result.appliedRules.filter(r => r.matched).length} of {result.appliedRules.length} matched)
                    </h4>
                    <div className="space-y-2">
                      {result.appliedRules.map((rule, idx) => (
                        <div
                          key={idx}
                          className={`rounded-lg p-3 ${
                            rule.matched 
                              ? 'bg-green-50 border border-green-200' 
                              : 'bg-gray-50 border border-gray-200'
                          }`}
                        >
                          <div className="flex items-center justify-between mb-1">
                            <div className="flex items-center gap-2">
                              {rule.matched ? (
                                <Check className="w-4 h-4 text-green-600" />
                              ) : (
                                <XCircle className="w-4 h-4 text-gray-400" />
                              )}
                              <span className="text-sm font-medium">
                                Rule #{rule.priority}
                              </span>
                              <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                                rule.matched ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'
                              }`}>
                                {rule.target}
                              </span>
                            </div>
                            <span className={`text-xs ${rule.matched ? 'text-green-600' : 'text-gray-400'}`}>
                              {rule.matched ? 'Matched' : 'No match'}
                            </span>
                          </div>

                          {rule.matched && rule.before && rule.after && (
                            <div className="mt-2 flex items-center gap-2 text-xs">
                              <code className="bg-white px-2 py-1 rounded text-gray-600 max-w-[120px] truncate" title={rule.before}>
                                {rule.before}
                              </code>
                              <ArrowRight className="w-3 h-3 text-gray-400" />
                              <code className="bg-white px-2 py-1 rounded text-green-700 max-w-[120px] truncate" title={rule.after}>
                                {rule.after}
                              </code>
                            </div>
                          )}

                          {!rule.matched && rule.reason && (
                            <p className="mt-1 text-xs text-gray-500">
                              {rule.reason}
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-gray-200 bg-gray-50 flex-shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-sm text-gray-500">
              <Info className="w-4 h-4" />
              <span>Rules are applied in priority order (lowest first)</span>
            </div>
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ManipulationTester;
